import { useCallback, useEffect, useMemo } from 'react';
import { translateEventToTicketGroupAccordionItem } from 'src/components/Accordions/TicketGroupsEventsAccordion';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import { ActiveSortTableColumnProvider } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { BulkEditHubContextProvider } from 'src/contexts/BulkEditHubContext';
import { CatalogDataContextProvider } from 'src/contexts/CatalogDataContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import {
  FilterQueryContextProvider,
  useFilterQueryContext,
} from 'src/contexts/FilterQueryContext';
import {
  MultiSelectionContextProvider,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { PurchaseDataContextProvider } from 'src/contexts/PurchaseDataContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { NoData } from 'src/tables/Table';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { DefaultPurchaseTableSorting } from 'src/utils/constants/tableColumns';
import { EmptyPurchaseQuery } from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { getCatalogDataExpanded } from 'src/utils/purchaseUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  ActionOutboxEntityType,
  PurchaseClient,
  PurchaseOrderDetails,
  PurchaseOrderQuery,
  PurchaseOrderState,
  UserSetting,
} from 'src/WebApiController';

import * as styles from '../../Events/EventPage/EventPage.css';
import { useAccordionItem } from '../../Events/EventPage/hooks/useAccordionItem';
import { SidePanelToolbar } from '../../Events/EventPage/SidePanel/SidePanelToolbar';
import { PurchaseSideTableFilterBar } from '../PurchaseFilterBar/PurchaseSideTableFilterBar';

const Purchases = ({
  viagVirtId,
  queryInput,
}: {
  viagVirtId: string;
  queryInput?: Partial<Pick<PurchaseOrderQuery, 'listingIds' | 'saleIds'>>;
}) => {
  const { isLoading, isItemsLoading, accordionItemProps } =
    useAccordionItem(viagVirtId);

  const { setSelectionMode } = useMultiSelectionContext();
  useEffect(() => {
    setSelectionMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viagVirtId]);

  const { setFilterQuery, filterQuery } =
    useFilterQueryContext<PurchaseOrderQuery>();
  useEffect(() => {
    setFilterQuery({
      ...filterQuery,
      ...queryInput,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput, setFilterQuery]);

  const purchasesAccordionItem = useMemo(
    () =>
      accordionItemProps &&
      translateEventToTicketGroupAccordionItem(accordionItemProps, 0, true),
    [accordionItemProps]
  );

  const tgIds = useMemo(
    () => purchasesAccordionItem?.entities?.map((tg) => Number(tg.tgId)) || [],
    [purchasesAccordionItem?.entities]
  );

  const hasSelectedEntities =
    (queryInput?.listingIds && queryInput?.listingIds?.length > 0) ||
    (queryInput?.saleIds && queryInput?.saleIds?.length > 0);

  const { BodyComponent, AfterHeaderComponent } = purchasesAccordionItem || {};
  const noData =
    (!isLoading && !purchasesAccordionItem) ||
    (!isItemsLoading && !accordionItemProps?.entities);
  return (
    <>
      <div className={styles.sidePanelToolbar}>
        <SidePanelToolbar
          sectionType={SectionType.PurchaseSideTable}
          filterBar={<PurchaseSideTableFilterBar />}
          noData={noData}
          entityIds={hasSelectedEntities ? tgIds : []}
        />
        {!noData && AfterHeaderComponent && <AfterHeaderComponent />}
      </div>
      {isLoading && <PosSpinner />}
      {noData ? (
        <NoData>
          <FormatContent
            id={FormatContentId.NoDataAvailable}
            params={<Content id={ContentId.Purchases} />}
          />
        </NoData>
      ) : (
        BodyComponent &&
        accordionItemProps && (
          <BodyComponent
            event={accordionItemProps.event}
            entities={accordionItemProps.entities.ticketGroups ?? []}
            entitiesSummary={accordionItemProps.entities.ticketGroupsSummary}
            entityCount={accordionItemProps.counts.tktGrpsCnt ?? 0}
            useTableNavKeys={true}
            useVirtuoso
            withOuterPadding={false}
            disablePagination={true}
            isSideTable={true}
          />
        )
      )}
    </>
  );
};

export const PurchasesSideTable = ({
  viagVirtId,
  queryInput,
}: {
  viagVirtId: string;
  queryInput?: Partial<Pick<PurchaseOrderQuery, 'listingIds' | 'saleIds'>>;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const getCatalogDataExpandedCallback = useCallback(
    (ids: string[], filterQuery: PurchaseOrderQuery) =>
      getCatalogDataExpanded(ids, filterQuery, {
        activeAccountWebClientConfig,
        onError: (error) => {
          trackError('PurchaseClient.getTicketGroupsForEvents', error, {
            ...filterQuery,
            eventIds: ids,
          });
        },
      }),
    [activeAccountWebClientConfig, trackError]
  );

  const getActivePosEntity = useCallback(
    async (purchaseId: number) => {
      const purchaseDetails = await new PurchaseClient(
        activeAccountWebClientConfig
      ).getPurchaseOrderByPurchaseOrderId(purchaseId, false);
      if (purchaseDetails) {
        return {
          posEntityId: purchaseDetails.id,
          posEntity: purchaseDetails,
          event: null,
          posEntityDisplayId: purchaseDetails.id.toString(),
        };
      }

      return {};
    },
    [activeAccountWebClientConfig]
  );

  return (
    <BulkEditHubContextProvider>
      <FilterQueryContextProvider<PurchaseOrderQuery>
        initialQuery={{
          ...EmptyPurchaseQuery,
          eventOrMappingIds: [viagVirtId],
          purchaseOrderState: PurchaseOrderState.Active,
        }}
        emptyQuery={EmptyPurchaseQuery}
        viewModeSettingId={UserSetting.PurchasePageViewMode}
        loadQueryFromUrl={false}
      >
        <CatalogDataContextProvider<PurchaseOrderQuery>
          entityType={ActionOutboxEntityType.Purchase}
          queryKey={'getCatalogForPurchaseOrders'}
          doNotLogPageLoad
          getCatalogDataExpanded={getCatalogDataExpandedCallback}
          transformEventData={transformData}
        >
          <ActivePosEntityProvider<PurchaseOrderDetails>
            entityType={ActionOutboxEntityType.Purchase}
            getActivePosEntity={getActivePosEntity}
            doNotLogPageLoad
          >
            <PurchaseDataContextProvider>
              <CatalogMultiSelectionContextProvider type="ticketGroup">
                <MultiSelectionContextProvider>
                  <ActiveSortTableColumnProvider
                    entityType={ActionOutboxEntityType.Purchase}
                    defaultSorting={DefaultPurchaseTableSorting}
                  >
                    <DialogProvider>
                      <Purchases
                        viagVirtId={viagVirtId}
                        queryInput={queryInput}
                      />
                    </DialogProvider>
                  </ActiveSortTableColumnProvider>
                </MultiSelectionContextProvider>
              </CatalogMultiSelectionContextProvider>
            </PurchaseDataContextProvider>
          </ActivePosEntityProvider>
        </CatalogDataContextProvider>
      </FilterQueryContextProvider>
    </BulkEditHubContextProvider>
  );
};
